/**
 * EForm Instance Reports 主頁面
 * 提供 eform instance 查詢、預覽與 PDF 匯出
 */

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  message,
  Spin,
  Progress,
  Typography,
  Modal,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Table,
  Tag
} from 'antd';
import {
  ReloadOutlined,
  FilePdfOutlined,
  EyeOutlined,
  SearchOutlined,
  SettingOutlined,
  PlusOutlined,
  MinusOutlined
} from '@ant-design/icons';
import { Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useLanguage } from '../../../contexts/LanguageContext';
import { TimezoneUtils } from '../../../utils/timezoneUtils';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { getFlatpickrDateFormat, parseDisplayToIso, isIsoDateString, dateToLocalIsoString, ensureFlatpickrStylesInDocument } from '../../../utils/eformDateUtils';
import { getEFormApprovalLabels } from '../../../utils/eformApprovalLabels';
import EFormInstanceSettings from './components/EFormInstanceSettings';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const getDefaultReportFilters = () => ({
  status: 'all',
  keyword: '',
  workflowDefinitionId: null,
  dateRange: [dayjs().startOf('day'), dayjs().endOf('day')]
});

const STATUS_ENDPOINTS = {
  pending: '/api/eforminstances/pending',
  approved: '/api/eforminstances/approved',
  rejected: '/api/eforminstances/rejected',
  manualPending: '/api/eforminstances/manual/pending',
  manualResponded: '/api/eforminstances/manual/responded'
};

const EFormInstanceReportsPage = () => {
  const { t } = useLanguage();

  const [filters, setFilters] = useState(getDefaultReportFilters);
  const [workflowDefinitions, setWorkflowDefinitions] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  /** 列表為前端分頁（資料來自各狀態 API 合併後的陣列）；受控分頁避免固定 pageSize 導致右下角每頁筆數無效 */
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [previewIframeSrc, setPreviewIframeSrc] = useState('');
  const [previewIframeLoaded, setPreviewIframeLoaded] = useState(0);
  const previewIframeRef = useRef(null);
  const flatpickrInstancesRef = useRef([]);

  // 展開行狀態（localStorage 記憶全部展開/收合偏好）
  const EXPAND_PREFERENCE_KEY = 'eformInstanceReportsExpandAllPreference';
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [fitToPage, setFitToPage] = useState(() => {
    try {
      const v = localStorage.getItem('eformReportPdfFitToPage');
      return v === 'true';
    } catch {
      return false;
    }
  });

  const statusOptions = useMemo(() => ([
    { value: 'all', label: t('common.all') || '全部' },
    { value: 'pending', label: t('common.pending') || '待處理' },
    { value: 'approved', label: t('common.approved') || '已批准' },
    { value: 'rejected', label: t('common.rejected') || '已拒絕' },
    { value: 'manualPending', label: t('reports.eformInstanceManualPending') || '待回應表單' },
    { value: 'manualResponded', label: t('reports.eformInstanceManualResponded') || '已收到回應表單' }
  ]), [t]);

  const loadWorkflowDefinitions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/workflowdefinitions?page=1&pageSize=1000', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          setWorkflowDefinitions(data.data);
        }
      }
    } catch (error) {
      console.error('載入流程定義失敗:', error);
    }
  }, []);

  const normalizeRecord = (record, sourceKey) => {
    let fieldDisplaySettings = record.fieldDisplaySettings ?? record.field_display_settings ?? null;
    if (typeof fieldDisplaySettings === 'string') {
      try { fieldDisplaySettings = JSON.parse(fieldDisplaySettings); } catch { fieldDisplaySettings = []; }
    }
    if (!Array.isArray(fieldDisplaySettings)) fieldDisplaySettings = [];

    const pdfExportCount = record.pdfExportCount ?? record.PdfExportCount ?? 0;
    return {
      ...record,
      id: record.id || record.Id,
      formName: record.formName || record.form_name,
      instanceName: record.instanceName || record.instance_name,
      status: record.status || record.formStatus || record.state || sourceKey,
      fillType: record.fillType || record.fill_type,
      createdAt: record.createdAt || record.created_at,
      fieldDisplaySettings,
      parsedFieldValues: record.parsedFieldValues ?? {},
      pdfExportCount: typeof pdfExportCount === 'number' ? pdfExportCount : 0,
      sourceKey
    };
  };

  /** 是否有多設定「列表中顯示」的欄位，可展開（fieldDisplaySettings 已在 normalizeRecord 預解析） */
  const hasExpandableFields = (record) => {
    return (record?.fieldDisplaySettings || []).some((f) => f.showInList);
  };

  /** 快取可展開的 row key 列表（dataSource 改變時才重新計算） */
  const expandableKeys = useMemo(
    () => (dataSource || []).filter(hasExpandableFields).map((r) => r.id),
    [dataSource]
  );

  const buildQueryParams = (currentFilters) => {
    const params = new URLSearchParams({
      page: '1',
      pageSize: '1000'
    });

    if (currentFilters.dateRange && currentFilters.dateRange.length === 2) {
      params.append('createdDateFrom', dayjs(currentFilters.dateRange[0]).toISOString());
      params.append('createdDateTo', dayjs(currentFilters.dateRange[1]).toISOString());
    }

    if (currentFilters.workflowDefinitionId) {
      params.append('workflowDefinitionId', String(currentFilters.workflowDefinitionId));
    }

    return params.toString();
  };

  const fetchByStatus = async (statusKey, currentFilters) => {
    const token = localStorage.getItem('token');
    const params = buildQueryParams(currentFilters);
    const endpoint = STATUS_ENDPOINTS[statusKey];

    if (!endpoint) {
      return [];
    }

    const response = await fetch(`${endpoint}?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return (data.data || data || []).map((record) => normalizeRecord(record, statusKey));
  };

  const applyKeywordFilter = (records, keyword) => {
    if (!keyword) {
      return records;
    }

    const lowered = keyword.toLowerCase();
    return records.filter((record) => {
      const formName = (record.formName || '').toLowerCase();
      const instanceName = (record.instanceName || '').toLowerCase();
      if (formName.includes(lowered) || instanceName.includes(lowered)) {
        return true;
      }
      // 關鍵字搜尋後端預提取的欄位值（無需 DOMParser）
      const parsedValues = record.parsedFieldValues || {};
      const visibleFields = (record.fieldDisplaySettings || []).filter((f) => f.showInList);
      for (const field of visibleFields) {
        const val = parsedValues[field.fieldId];
        if (val != null && String(val).toLowerCase().includes(lowered)) {
          return true;
        }
      }
      return false;
    });
  };

  const loadReportData = useCallback(async (currentFilters) => {
    setLoading(true);
    setProgress({ current: 0, total: 0, message: t('reports.loading') || '載入中...' });

    try {
      const statusKeys = currentFilters.status === 'all'
        ? Object.keys(STATUS_ENDPOINTS)
        : [currentFilters.status];

      const results = await Promise.all(statusKeys.map((statusKey) => fetchByStatus(statusKey, currentFilters)));
      const merged = results.flat();

      const uniqueMap = new Map();
      merged.forEach((record) => {
        if (!record.id) {
          return;
        }
        if (!uniqueMap.has(record.id)) {
          uniqueMap.set(record.id, record);
        }
      });

      const uniqueRecords = Array.from(uniqueMap.values()).sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeB - timeA;
      });

      const filtered = applyKeywordFilter(uniqueRecords, currentFilters.keyword);
      setDataSource(filtered);
      setTablePage(1);
      setSelectedRowKeys([]);

      if (filtered.length === 0) {
        message.info(t('reports.eformInstancesNoData') || '沒有符合條件的表單資料');
      } else {
        message.success((t('reports.totalRecords') || '共 {total} 條記錄').replace('{total}', filtered.length));
      }
    } catch (error) {
      console.error('載入 eform instance 報表失敗:', error);
      message.error(t('reports.loadDataFailed') || '載入報表數據失敗');
      setDataSource([]);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  }, [t]);

  useEffect(() => {
    loadWorkflowDefinitions();
    loadReportData(getDefaultReportFilters());
  }, [loadReportData, loadWorkflowDefinitions]);

  /** 全部展開/收合（並寫入 localStorage 記憶） */
  const handleToggleExpandAll = () => {
    const keys = expandableKeys;
    const allExpanded = keys.length > 0 && keys.every((k) => expandedRowKeys.includes(k));
    if (allExpanded) {
      setExpandedRowKeys([]);
      try {
        localStorage.setItem(EXPAND_PREFERENCE_KEY, 'collapsed');
      } catch (_) {}
    } else {
      setExpandedRowKeys(keys);
      try {
        localStorage.setItem(EXPAND_PREFERENCE_KEY, 'expanded');
      } catch (_) {}
    }
  };

  /** 資料載入後依 localStorage 偏好還原展開/收合 */
  useEffect(() => {
    let preference = 'collapsed';
    try {
      preference = localStorage.getItem(EXPAND_PREFERENCE_KEY) || 'collapsed';
    } catch (_) {}
    if (preference === 'expanded' && expandableKeys.length > 0) {
      setExpandedRowKeys(expandableKeys);
    } else {
      setExpandedRowKeys([]);
    }
  }, [expandableKeys]);

  const handleSearch = () => {
    loadReportData(filters);
  };

  /** 將相對路徑的 img src 改為絕對 URL，供後端 LibreOffice 轉 PDF 時可載入圖片 */
  const htmlWithAbsoluteImageUrls = (html, baseUrl) => {
    if (!html) return '';
    return html.replace(
      /<img([^>]*)\ssrc=["']([^"']+)["']([^>]*)>/gi,
      (match, before, src, after) => {
        if (src && src.startsWith('/') && !src.startsWith('//') && !/^https?:\/\//i.test(src)) {
          return `<img${before} src="${baseUrl}${src}"${after}>`;
        }
        return match;
      }
    );
  };

  const handleExportPdf = async () => {
    if (dataSource.length === 0) {
      message.warning(t('reports.noDataToExport') || '沒有數據可導出');
      return;
    }

    const selectedRecords = dataSource.filter((record) => selectedRowKeys.includes(record.id));
    if (selectedRecords.length === 0) {
      message.warning(t('reports.noDataToExport') || '沒有數據可導出');
      return;
    }

    setExporting(true);
    setProgress({
      current: 0,
      total: selectedRecords.length,
      message: t('reports.loading') || '載入表單內容...'
    });

    const baseUrl = window.location.origin;
    let htmlContents;
    try {
      const htmlList = await Promise.all(selectedRecords.map((r) => fetchInstanceHtml(r.id)));
      htmlContents = htmlList.map((html) => htmlWithAbsoluteImageUrls(html, baseUrl));
    } catch (e) {
      message.error(t('reports.loadDataFailed') || '取得表單內容失敗');
      setExporting(false);
      setProgress({ current: 0, total: 0, message: '' });
      return;
    }

    setProgress({
      current: selectedRecords.length,
      total: selectedRecords.length,
      message: t('reports.exportingPdf') || '正在透過 LibreOffice 轉換 PDF...'
    });

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/reports/eform-instances/export-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          htmlContents,
          fitToPage,
          instanceIds: selectedRecords.map((r) => r.id)
        })
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const errMsg = errBody?.error || response.statusText || `HTTP ${response.status}`;
        throw new Error(errMsg);
      }

      const blob = await response.blob();
      const today = TimezoneUtils.formatDateTime(new Date(), 'YYYY-MM-DD');
      const fileName = `eform_instance_reports_${today}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);

      message.success(t('reports.exportSuccess') || '匯出成功');
    } catch (error) {
      console.error('PDF 導出失敗:', error);
      message.error(t('reports.exportFailed') || `匯出失敗: ${error.message}`);
    } finally {
      setExporting(false);
      setProgress({ current: 0, total: 0, message: '' });
    }
  };

  /** 根據 HTML 字串建立 iframe Blob URL */
  const buildIframeSrc = (html) => {
    if (!html) return '';
    const baseUrl = window.location.origin;
    const processedHtml = html.replace(
      /<img([^>]*)\ssrc=["']([^"']+)["']([^>]*)>/gi,
      (match, before, src, after) => {
        if (src && src.startsWith('/') && !src.startsWith('//') && !src.match(/^https?:\/\//i)) {
          return `<img${before} src="${baseUrl}${src}"${after}>`;
        }
        return match;
      }
    );
    const fullHtml = `<!DOCTYPE html>
<html lang="zh-TW">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<base href="${baseUrl}/">
<style>
  body { margin: 0; padding: 20px; font-family: Arial, sans-serif; background-color: #fff; }
  * { box-sizing: border-box; }
  table { width: 100% !important; max-width: 100% !important; border-collapse: collapse; }
  img { max-width: 100% !important; height: auto !important; }
  div, p, section, article { max-width: 100% !important; }
  form { width: 100% !important; max-width: 100% !important; }
</style>
</head>
<body>
${processedHtml}
</body>
</html>`;
    const blob = new Blob([fullHtml], { type: 'text/html' });
    return URL.createObjectURL(blob);
  };

  /** 按需從後端取得完整 HTML（列表 API 已不回傳 HTML，只在 Preview 時才取） */
  const fetchInstanceHtml = async (id) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`/api/eforminstances/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const detail = await response.json();
    return detail.htmlCode || detail.filledHtmlCode || '';
  };

  const openPreview = async (record) => {
    setPreviewRecord(record);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewIframeSrc('');
    try {
      const html = await fetchInstanceHtml(record.id);
      const url = buildIframeSrc(html);
      setPreviewIframeSrc(url);
    } catch (e) {
      message.error(t('reports.loadDataFailed') || '載入預覽失敗');
    } finally {
      setPreviewLoading(false);
    }
  };

  // 清理 iframe src 當 Modal 關閉時
  const handlePreviewClose = () => {
    if (previewIframeSrc) {
      URL.revokeObjectURL(previewIframeSrc);
      setPreviewIframeSrc('');
    }
    setPreviewOpen(false);
    setPreviewRecord(null);
    flatpickrInstancesRef.current.forEach(fp => fp?.destroy?.());
    flatpickrInstancesRef.current = [];
  };

  // 預覽 iframe 載入後對 date 欄位初始化 Flatpickr
  useEffect(() => {
    if (!previewOpen || !previewIframeSrc || !previewIframeRef.current) return;
    const iframe = previewIframeRef.current;
    const container = iframe.contentDocument?.body;
    if (!container) return;
    flatpickrInstancesRef.current.forEach(fp => fp?.destroy?.());
    flatpickrInstancesRef.current = [];
    ensureFlatpickrStylesInDocument(container);
    const dateInputs = container.querySelectorAll('input[data-input-type="date"]');
    dateInputs.forEach(el => {
      try {
        const formatKey = el.getAttribute('data-date-format') || 'YYYY-MM-DD';
        const fpFormat = getFlatpickrDateFormat(formatKey);
        const dataValue = (el.getAttribute('data-value') || '').trim();
        const rawValue = (el.value || el.getAttribute('value') || '').trim();
        let defaultDate = null;
        if (dataValue && isIsoDateString(dataValue)) defaultDate = dataValue;
        else if (rawValue) {
          if (isIsoDateString(rawValue)) defaultDate = rawValue;
          else {
            const iso = parseDisplayToIso(rawValue, formatKey);
            if (iso) defaultDate = iso;
          }
        }
        const defaultDateObj = defaultDate ? new Date(defaultDate + 'T12:00:00') : null;
        const validDefaultDate = defaultDateObj && !isNaN(defaultDateObj.getTime()) ? defaultDateObj : (defaultDate || undefined);
        const fp = flatpickr(el, {
          dateFormat: fpFormat,
          defaultDate: validDefaultDate,
          allowInput: true,
          appendTo: container,
          onChange(selectedDates, dateStr) {
            el.setAttribute('value', dateStr);
            if (selectedDates[0]) el.setAttribute('data-value', dateToLocalIsoString(selectedDates[0]));
          }
        });
        flatpickrInstancesRef.current.push(fp);
      } catch (err) {
        console.warn('Flatpickr init failed for date input:', err);
      }
    });
    return () => {
      flatpickrInstancesRef.current.forEach(fp => fp?.destroy?.());
      flatpickrInstancesRef.current = [];
    };
  }, [previewOpen, previewIframeSrc, previewIframeLoaded]);

  /** 展開行：顯示「表單欄位設定為要顯示」的欄位與值（直接使用後端預提取的 parsedFieldValues） */
  const renderExpandedRow = (record) => {
    const visibleFields = (record.fieldDisplaySettings || []).filter((f) => f.showInList);
    if (visibleFields.length === 0) {
      return (
        <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
          {t('reports.noFieldSummary') || '無欄位摘要設定'}
        </div>
      );
    }
    return (
      <div style={{ padding: '8px', backgroundColor: '#fafafa' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${visibleFields.length}, 1fr)`,
            gap: '4px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            padding: '6px',
            border: '1px solid #e8e8e8'
          }}
        >
          {visibleFields.map((field) => (
            <div
              key={`header-${field.fieldId}`}
              style={{
                fontWeight: '600',
                color: '#333',
                fontSize: '12px',
                padding: '4px',
                backgroundColor: '#f5f5f5',
                borderRadius: '3px',
                textAlign: 'center',
                border: '1px solid #d9d9d9'
              }}
            >
              {field.displayLabel || field.fieldId}
            </div>
          ))}
          {visibleFields.map((field) => {
            const val = (record.parsedFieldValues || {})[field.fieldId];
            return (
              <div
                key={`value-${field.fieldId}`}
                style={{
                  color: '#666',
                  fontSize: '12px',
                  padding: '4px',
                  backgroundColor: '#fff',
                  borderRadius: '3px',
                  textAlign: 'center',
                  border: '1px solid #e8e8e8',
                  minHeight: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {val != null && val !== '' ? String(val) : '-'}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const columns = useMemo(() => ([
    {
      title: t('common.formName') || '表單名稱',
      dataIndex: 'formName',
      key: 'formName',
      width: 200,
      ellipsis: true
    },
    {
      title: t('common.instanceName') || '實例名稱',
      dataIndex: 'instanceName',
      key: 'instanceName',
      width: 200,
      ellipsis: true
    },
    {
      title: t('common.status') || '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => {
        const normalized = status || '-';
        const colorMap = {
          Pending: 'orange',
          Approved: 'green',
          Rejected: 'red',
          Submitted: 'blue'
        };
        const approvalLabels = getEFormApprovalLabels(record, t, {
          approve: 'common.approved',
          reject: 'common.rejected'
        });
        const displayText = normalized === 'Approved'
          ? approvalLabels.approve
          : normalized === 'Rejected'
            ? approvalLabels.reject
            : normalized === 'Pending'
              ? (t('common.pending') || '待處理')
              : normalized === 'Submitted'
                ? (t('common.submitted') || '已提交')
                : normalized;
        return <Tag color={colorMap[normalized] || 'default'}>{displayText}</Tag>;
      }
    },
    {
      title: t('common.fillType') || '填寫類型',
      dataIndex: 'fillType',
      key: 'fillType',
      width: 140
    },
    {
      title: t('reports.pdfExportCount') || '匯出次數',
      dataIndex: 'pdfExportCount',
      key: 'pdfExportCount',
      width: 100,
      align: 'center',
      render: (val) => (typeof val === 'number' ? val : 0)
    },
    {
      title: t('common.createdAt') || '創建時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (value) => (
        value ? TimezoneUtils.formatDateTime(value, 'YYYY-MM-DD HH:mm:ss') : '-'
      )
    },
    {
      title: t('common.action') || '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          size="small"
          onClick={() => openPreview(record)}
        >
          {t('reports.eformInstancesPreview') || '預覽'}
        </Button>
      )
    }
  ]), [t]);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    }
  }), [selectedRowKeys]);

  // 清理 iframe src 當組件卸載時
  useEffect(() => {
    return () => {
      if (previewIframeSrc) {
        URL.revokeObjectURL(previewIframeSrc);
      }
    };
  }, [previewIframeSrc]);

  return (
    <div style={{ padding: '24px' }}>
      <style>{`
        .eform-instance-reports-table .eform-row-not-exported:hover > td { background-color: #fff1b8 !important; }
        .eform-instance-reports-table .eform-row-exported:hover > td { background-color: #c8e6c9 !important; }
      `}</style>
      <Card style={{ marginBottom: '16px' }} bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0 }}>
              {t('reports.eformInstancesTitle') || 'EForm Instance 報表'}
            </h2>
            <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
              {t('reports.eformInstancesDescription') || '查詢並預覽表單填寫結果，支援一頁一張 PDF 匯出'}
            </p>
          </div>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadReportData(filters)}
              loading={loading}
              disabled={exporting}
            >
              {t('common.refresh') || '刷新'}
            </Button>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              onClick={handleExportPdf}
              loading={exporting}
              disabled={loading || dataSource.length === 0 || selectedRowKeys.length === 0}
            >
              {t('common.exportPdf') || '匯出 PDF'}
            </Button>
            <Tooltip title={t('reports.printOutSettings') || 'Print Out Settings'}>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setSettingsModalOpen(true)}
              />
            </Tooltip>
          </Space>
        </div>
      </Card>

      {(loading || exporting) && progress.total > 0 && (
        <Card size="small" style={{ marginBottom: '16px' }}>
          <Progress
            percent={progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0}
            status="active"
            format={() => `${progress.current}/${progress.total}`}
          />
          <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
            {progress.message}
          </div>
        </Card>
      )}

      <Card
        title={t('reports.eformInstancesFilters') || '查詢條件'}
        size="small"
        style={{ marginBottom: '16px' }}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: '8px' }}>
              <Text>{t('calendar.fieldWorkflow') || '流程定義'}</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              placeholder={t('reports.realtime.selectWorkflow') || '選擇流程定義'}
              value={filters.workflowDefinitionId}
              onChange={(value) => setFilters((prev) => ({ ...prev, workflowDefinitionId: value ?? null }))}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {workflowDefinitions.map((wf) => (
                <Option key={wf.id} value={wf.id}>
                  {wf.name || `#${wf.id}`}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: '8px' }}>
              <Text>{t('reports.eformInstancesKeyword') || '關鍵字'}</Text>
            </div>
            <Input
              placeholder={t('reports.eformInstancesKeywordPlaceholder') || '表單名稱 / 實例名稱 / 表單欄位內容'}
              value={filters.keyword}
              onChange={(event) => setFilters((prev) => ({ ...prev, keyword: event.target.value }))}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: '8px' }}>
              <Text>{t('reports.eformInstancesStatus') || '狀態'}</Text>
            </div>
            <Select
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
            >
              {statusOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: '8px' }}>
              <Text>{t('reports.dateRange') || '時間範圍'}</Text>
            </div>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange}
              onChange={(values) => setFilters((prev) => ({ ...prev, dateRange: values }))}
              showTime
              format="YYYY-MM-DD HH:mm:ss"
            />
          </Col>
        </Row>
        <div style={{ marginTop: '16px', textAlign: 'right' }}>
          <Space>
            <Button
              icon={<SearchOutlined />}
              type="primary"
              onClick={handleSearch}
              loading={loading}
            >
              {t('common.search') || '查詢'}
            </Button>
            <Button
              onClick={() => {
                setFilters(getDefaultReportFilters());
                setDataSource([]);
                setTablePage(1);
                setSelectedRowKeys([]);
              }}
            >
              {t('common.reset') || '重置'}
            </Button>
          </Space>
        </div>
      </Card>

      <Spin spinning={loading} tip={t('reports.loading') || '載入中...'}>
        <Card>
          {dataSource.length > 0 && expandableKeys.length > 0 && (
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title={t('reports.expandCollapseAll') || '全部展開/收合'}>
                <Button
                  type="text"
                  size="small"
                  icon={
                    expandableKeys.length > 0 &&
                    expandableKeys.every((k) => expandedRowKeys.includes(k))
                      ? <MinusOutlined />
                      : <PlusOutlined />
                  }
                  onClick={handleToggleExpandAll}
                >
                  {t('reports.expandCollapseAll') || '全部展開/收合'}
                </Button>
              </Tooltip>
            </div>
          )}
          <Table
            rowKey="id"
            columns={columns}
            dataSource={dataSource}
            rowSelection={rowSelection}
            pagination={{
              current: tablePage,
              pageSize: tablePageSize,
              total: dataSource.length,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total) =>
                (t('reports.totalRecords') || '共 {total} 條記錄').replace('{total}', String(total)),
              onChange: (p, ps) => {
                setTablePage(p);
                setTablePageSize(ps);
              }
            }}
            locale={{ emptyText: t('reports.eformInstancesNoData') || '沒有符合條件的表單資料' }}
            scroll={{ x: 900 }}
            className="eform-instance-reports-table"
            rowClassName={(record) => (record.pdfExportCount ?? 0) > 0 ? 'eform-row-exported' : 'eform-row-not-exported'}
            onRow={(record) => ({
              style: {
                backgroundColor: (record.pdfExportCount ?? 0) > 0 ? '#e8f5e9' : '#fffbe6'
              }
            })}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: setExpandedRowKeys,
              expandedRowRender: renderExpandedRow,
              expandIcon: ({ expanded, onExpand, record }) => {
                if (!hasExpandableFields(record)) return null;
                return (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    style={{ cursor: 'pointer', marginRight: 8 }}
                  >
                    {expanded ? <MinusOutlined /> : <PlusOutlined />}
                  </span>
                );
              }
            }}
          />
        </Card>
      </Spin>

      <Modal
        title={t('reports.eformInstancesPreview') || '預覽'}
        open={previewOpen}
        onCancel={handlePreviewClose}
        footer={[
          <Button key="close" onClick={handlePreviewClose}>
            {t('common.close') || '關閉'}
          </Button>
        ]}
        width="90vw"
        style={{ top: 24 }}
        destroyOnClose
      >
        <Spin spinning={previewLoading} tip={t('reports.loading') || '載入中...'}>
          {previewIframeSrc ? (
            <iframe
              ref={previewIframeRef}
              src={previewIframeSrc}
              onLoad={() => setPreviewIframeLoaded(n => n + 1)}
              style={{
                width: '100%',
                height: '75vh',
                border: '1px solid #d9d9d9',
                borderRadius: '8px',
                backgroundColor: '#fff'
              }}
              title="EForm Preview"
            />
          ) : (
            !previewLoading && (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                {t('reports.eformInstancesNoPreview') || '沒有可預覽的內容'}
              </div>
            )
          )}
        </Spin>
      </Modal>

      {/* PDF 匯出設定（含縮放以塞進一頁） */}
      <EFormInstanceSettings
        open={settingsModalOpen}
        onClose={() => setSettingsModalOpen(false)}
        fitToPage={fitToPage}
        onFitToPageChange={(v) => {
          setFitToPage(v);
          try {
            localStorage.setItem('eformReportPdfFitToPage', String(v));
          } catch (_) {}
        }}
      />
    </div>
  );
};

export default EFormInstanceReportsPage;
